import { useState, useEffect, useCallback } from 'react';
import permissionService, { UserPermissions } from '../services/permission.service';

export const usePermissions = () => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const data = await permissionService.getUserPermissions(forceRefresh);
      setPermissions(data);
    } catch (err) {
      setError('Failed to load permissions');
      // Silent fail - permission errors are handled by the UI
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const checkPermission = useCallback(
    async (
      action: string,
      objectType?: string,
      objectId?: string,
      context?: {
        company_id?: string;
        team_id?: string;
        department_id?: string;
      }
    ) => {
      const result = await permissionService.checkPermission(
        action,
        objectType,
        objectId,
        context
      );
      return result.has_permission;
    },
    []
  );

  const refresh = useCallback(() => {
    return loadPermissions(true);
  }, [loadPermissions]);

  // Simple permission check function
  const hasPermission = useCallback((model: string, action: string) => {
    // Superusers have all permissions
    if (permissions?.user.is_superuser) return true;
    
    // Check in model permissions
    const modelPerms = permissions?.model_permissions?.[model];
    if (!modelPerms) return false;
    
    return modelPerms.includes(action) || modelPerms.includes('all');
  }, [permissions]);

  return {
    permissions,
    loading,
    error,
    checkPermission,
    hasPermission,
    refresh,
    
    // Convenience properties
    canCreateRisk: permissions?.permissions.can_create_risk || false,
    canCreateTask: permissions?.permissions.can_create_task || false,
    canExport: permissions?.permissions.can_export || false,
    canApprove: permissions?.permissions.can_approve || false,
    canAssign: permissions?.permissions.can_assign || false,
    isAdmin: permissions?.permissions.is_admin || false,
    isManager: permissions?.permissions.is_manager || false,
    isSuperuser: permissions?.user.is_superuser || false,
    isStaff: permissions?.user.is_staff || false,
  };
};

// Default actions to prevent re-creation on every render
const DEFAULT_ACTIONS = ['update', 'delete'];

// Hook for checking permissions on a specific object
export const useObjectPermission = (
  objectType: string,
  objectId?: string | null,
  actions: string[] = DEFAULT_ACTIONS
) => {
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  
  // Memoize actions to prevent infinite loops
  const actionsKey = actions.join(',');

  useEffect(() => {
    if (!objectId) {
      setPermissions({});
      setLoading(false);
      return;
    }

    const checkPermissions = async () => {
      setLoading(true);
      const results: Record<string, boolean> = {};

      await Promise.all(
        actions.map(async (action) => {
          const result = await permissionService.checkPermission(
            action,
            objectType,
            objectId
          );
          results[action] = result.has_permission;
        })
      );

      setPermissions(results);
      setLoading(false);
    };

    checkPermissions();
  }, [objectType, objectId, actionsKey]); // Use actionsKey instead of actions array

  return {
    permissions,
    loading,
    canUpdate: permissions.update || false,
    canDelete: permissions.delete || false,
  };
};